/**
 * @file Final grade pass for the neon arena look (GDD §Fase 4).
 *
 * One cheap fullscreen pass doing four things that together sell the "arcade
 * cabinet in a dark room" feel: chromatic aberration that grows toward the
 * edges, a vignette, faint scanlines and a saturation lift. Written by hand
 * rather than pulled from a library — it is 30 lines of GLSL.
 */

export const NeonGradeShader = {
  name: 'NeonGradeShader',

  uniforms: {
    tDiffuse: { value: null },
    uResolution: { value: [1, 1] },
    uVignette: { value: 0.4 },
    uAberration: { value: 0.0015 },
    uFisheye: { value: 0 },
    uScanline: { value: 0.05 },
    uSaturation: { value: 1.1 },
    uTime: { value: 0 }
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 uResolution;
    uniform float uVignette;
    uniform float uAberration;
    uniform float uFisheye;
    uniform float uScanline;
    uniform float uSaturation;
    uniform float uTime;

    varying vec2 vUv;

    void main() {
      vec2 centred = vUv - 0.5;
      float radius = length(centred);
      vec2 warpedUv = clamp(0.5 + centred * (1.0 + uFisheye * radius * radius), vec2(0.001), vec2(0.999));

      // Chromatic aberration: channels drift apart the further from centre we
      // are, which reads as a cheap lens without any distortion cost.
      vec2 offset = centred * radius * uAberration * 20.0;
      vec3 color;
      color.r = texture2D(tDiffuse, clamp(warpedUv + offset, vec2(0.001), vec2(0.999))).r;
      color.g = texture2D(tDiffuse, warpedUv).g;
      color.b = texture2D(tDiffuse, clamp(warpedUv - offset, vec2(0.001), vec2(0.999))).b;

      // Saturation around luma.
      float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      color = mix(vec3(luma), color, uSaturation);

      // Scanlines, keyed to physical pixels so they do not crawl on resize.
      float scan = sin(vUv.y * uResolution.y * 1.6 + uTime * 0.6);
      color *= 1.0 - uScanline * (0.5 + 0.5 * scan);

      // Vignette.
      color *= 1.0 - uVignette * smoothstep(0.28, 0.78, radius);

      gl_FragColor = vec4(color, 1.0);
    }
  `
};
